import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useMemo, useState } from 'react'
import {
  CheckIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ListBulletIcon,
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import moment from 'moment-timezone'
import dayjs from 'dayjs'
import TimePicker from 'rc-time-picker'
import 'rc-time-picker/assets/index.css'

import ModalContainer from 'components/ModalContainer'
import { Button, Card, Input, Table } from 'components/gsys-ui'
import { useToggleHideInv } from 'util/mutations'
import { useInvoices } from 'util/queries'
import { successToast } from 'util/helpers'
import { PaginatedTable } from 'components/gsys-ui/Table/Table'

moment.tz.setDefault('Europe/London')

const HiddenInvoiceModal = NiceModal.create(({ branch }) => {
  const modal = useModal()
  const [search, setSearch] = useState('')
  const { isLoading, isError, error, data } = useInvoices('hidden')

  if (isLoading) return null

  const dataSearched = data.filter((row) =>
    `${row.Document} ${row.Acct} ${row.Name}`.toLowerCase().includes(search.toLowerCase().trim())
  )

  return (
    <ModalContainer>
      <Card className="!w-[1000px]">
        <Card.Title Icon={ClockIcon}>
          <div>Hidden invoices</div>
          <div className="flex items-center space-x-2">
            <div className="w-48">
              <Input
                className="!py-0.5 !h-8"
                placeholder="Type to search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button onClick={modal.remove}>Close</Button>
          </div>
        </Card.Title>
        <Card.Body>
          <InvTable data={dataSearched} />
        </Card.Body>
      </Card>
    </ModalContainer>
  )
})

const InvTable = (props) => {
  const hideMutation = useToggleHideInv()

  const columns = useMemo(
    () => [
      {
        header: '',
        accessorKey: 'lines',
        cellSize: 1,
        disableSortBy: true,
        noPadAnywhere: true,
        cell: ({ row }) => (
          <div className="flex items-center h-full" data-no-dnd="true">
            <Button
              variant="rounded"
              tiny
              onClick={() =>
                NiceModal.show('assignmentViewLinesModal', { docNo: row.original.Document })
              }
              Icon={ListBulletIcon}
            />
          </div>
        )
      },
      {
        header: '',
        accessorKey: 'warn',
        cellSize: 1,
        noPadAnywhere: true,
        cell: ({ row }) =>
          row.original.Loc ? null : (
            <div className="flex items-center" title="Customer does not have a loc">
              <ExclamationCircleIcon className="mr-1 w-5 h-5 text-red-500" />
            </div>
          )
      },
      {
        header: 'TIME',
        id: 'time',
        cell: ({ row }) => moment.tz(row.original.DateTime, 'Etc/UTC').format('HH:mm DD/MM/YY')
      },
      {
        header: 'ACCT',
        accessorKey: 'Name',
        cell: ({ row }) => (
          <span>
            <span className="font-bold">[{row.original.Acct}]</span> {row.original.Name}
          </span>
        )
      },
      {
        header: 'DOC',
        accessorKey: 'Document'
      },
      {
        header: 'LINES',
        accessorKey: 'LinesCount'
      },
      {
        header: 'ITEMS',
        accessorKey: 'ItemsQty'
      },
      {
        header: 'VALUE',
        accessorKey: 'Goods',
        cell: ({ row }) =>
          row.original.Goods ? `£${parseFloat(row.original.Goods).toFixed(2)}` : '£0.00'
      },
      {
        header: '',
        id: 'actions',
        cell: ({ row }) => {
          return (
            <div className="flex items-end" data-no-dnd="true">
              <Button
                tiny
                onClick={async () => {
                  console.log('onclick')
                  await hideMutation.mutateAsync(row.original.Document)
                  successToast(`Invoice ${row.original.Document} was unhidden.`)
                }}
              >
                Unhide
              </Button>
            </div>
          )
        }
      }
    ],
    [props.data]
  )

  const data = useMemo(() => props.data, [props.data])

  return (
    <PaginatedTable
      cols={columns}
      data={data}
      pageSize={20}
      isCompact={true}
      thinRows={true}
      notFull={true}
      isPaginated={true}
      dragData={props.dragData}
      totalRowsCount={data.length}
    />
  )
}

export default HiddenInvoiceModal
